import React, { useState } from 'react';
import CaseTypeDetailForm, {
	CaseTypeDetailFormValues,
} from './CaseTypeDetailForm';
import apiClient from '../../../../../Services/ApiClient';
import useApiClient from '../../../../Base/Hooks/useApiClient';
import { parseBoolean } from '../../../../../Utils/form.util';
import { createPriceObject } from '../../../../../Utils/price.util';
import { useIsLatam } from '../../../../Region/Services/region.service';
import { useAppContext } from '../../../../../Services/AppContext';
import useTranslation from '../../../../../Hooks/useTranslation';
import { useEffectAsync } from '../../../../../helpers';
import Loading from '../../../../../Components/Loading';
import useCaseTypes from '../../../Hooks/useCaseTypes';
import { getFormattedPriceAsFloat } from '../../../../Base/Services/priceFormat.service';
import useDataFormatter from '../../../../Base/Hooks/useDataFormatter';

interface Props {
	caseTypeId?: string;
	onSuccess: (canBookOnline: boolean) => void;
	workplaceId: string;
	isOnlyOneRoot: boolean;
}

const CaseTypeDetail: React.FC<Props> = ({
	caseTypeId,
	workplaceId,
	onSuccess,
	isOnlyOneRoot,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [defaultValues, setDefaultValues] =
		useState<CaseTypeDetailFormValues>();
	const { displayError, displaySuccess } = useApiClient();
	const { globalTemplate, currency, vatRate } = useAppContext();
	const isLatam = useIsLatam(globalTemplate);
	const { t } = useTranslation();
	const { getDefaultsValuesForCaseType } = useCaseTypes();
	const { remapObjectToArray } = useDataFormatter();

	// GETTING DEFAULT VALUES
	useEffectAsync(async () => {
		if (caseTypeId) {
			const defaults = await getDefaultsValuesForCaseType(caseTypeId);
			if (defaults) {
				setDefaultValues(defaults);
			}
		}
		setIsLoading(false);
	}, [caseTypeId]);

	const createCaseTypeData = (values: CaseTypeDetailFormValues) => {
		let price = null;

		if (parseBoolean(values.isPayed) || isLatam) {
			if (values.price) {
				const vRate =
					typeof values.vatRate?.value === 'string'
						? parseFloat(values.vatRate?.value)
						: vatRate;

				price = createPriceObject(
					getFormattedPriceAsFloat(values.price),
					currency,
					vRate,
				);
			}
		}

		return {
			type: 'caseType',
			label: values.label,
			label_translations: remapObjectToArray(values.labelTranslations),
			parentCaseTypeId: values?.parentCaseTypeId?.value ?? null,
			canBookOnline: parseBoolean(values.canBook)
				? values.canBookOnline
				: false,
			canBookOnSite: parseBoolean(values.canBook)
				? values.canBookOnSite
				: false,
			eventInterval: values?.eventInterval,
			canOrderImmediate: parseBoolean(values.canOrderImmediate),
			hiddenForPatient: parseBoolean(values.hiddenForPatient),
			price: price,
			notificationCase: parseBoolean(values.notificationCase),
			notificationBook: parseBoolean(values.notificationBook),
			code: parseBoolean(values?.setupForMonitoringCenter)
				? values?.code
				: null,
		};
	};

	const handleCreate = async (values: CaseTypeDetailFormValues) => {
		try {
			const caseType = await apiClient.post(
				`/workplaces/${workplaceId}/case/type`,
				{
					caseType: createCaseTypeData(values),
				},
			);

			if (caseType) {
				if (parseBoolean(values.isWithDoc)) {
					const withDocuments = await apiClient.post(
						`/case/type/${caseType.data.id}/item`,
						{
							caseTypeId: caseType.data.id,
							title: values?.docName,
							key: 'isWithDoc',
							type: 'upload',
							settings: {
								required: parseBoolean(values?.docRequired),
							},
						},
					);
				}

				if (parseBoolean(values.isWithAnswer)) {
					const withAnswer = await apiClient.post(
						`/case/type/${caseType.data.id}/item`,
						{
							caseTypeId: caseType.data.id,
							title: values?.answerName,
							key: 'isWithAnswer',
							type: 'textfield',
							settings: {
								required: parseBoolean(values?.answerRequired),
							},
						},
					);
				}
			}
			displaySuccess(
				t('component.caseType.caseTypeSuccessfullyCreated') ?? 'Created',
			);
			onSuccess(parseBoolean(values.canBook));
		} catch (e: any) {
			displayError(e);
		}
	};

	const handleEdit = async (values: CaseTypeDetailFormValues) => {
		try {
			const caseType = await apiClient.patch(
				`/workplaces/${workplaceId}/case/type/${caseTypeId}`,
				{
					caseType: createCaseTypeData(values),
				},
			);

			if (caseType && caseTypeId && values?.caseTypeId) {
				if (parseBoolean(values.isWithDoc)) {
					const docData = {
						caseTypeId: values.caseTypeId,
						title: values?.docName,
						key: 'isWithDoc',
						type: 'upload',
						settings: {
							required: parseBoolean(values?.docRequired),
						},
					};
					if (values.docId) {
						const withDocuments = await apiClient.patch(
							`/case/type/${values.caseTypeId}/item/${values.docId}`,
							docData,
						);
					} else {
						const withDocuments = await apiClient.post(
							`/case/type/${values.caseTypeId}/item`,
							docData,
						);
					}
				} else {
					if (values.docId) {
						await apiClient.delete(
							`/case/type/${values.caseTypeId}/item/${values.docId}`,
						);
					}
				}

				if (parseBoolean(values.isWithAnswer)) {
					const answerData = {
						caseTypeId: values.caseTypeId,
						title: values?.answerName,
						key: 'isWithAnswer',
						type: 'textfield',
						settings: {
							required: parseBoolean(values?.answerRequired),
						},
					};
					if (values.answerId) {
						const withAnswer = await apiClient.patch(
							`/case/type/${values.caseTypeId}/item/${values.answerId}`,
							answerData,
						);
					} else {
						const withAnswer = await apiClient.post(
							`/case/type/${values.caseTypeId}/item`,
							answerData,
						);
					}
				} else {
					if (values.answerId) {
						await apiClient.delete(
							`/case/type/${values.caseTypeId}/item/${values.answerId}`,
						);
					}
				}
			}
			displaySuccess(
				t('component.caseType.caseTypeSuccessfullyEdited') ?? 'Edited',
			);
			onSuccess(parseBoolean(values.canBook));
		} catch (e: any) {
			displayError(e);
		}
	};

	const handleRemove = async () => {
		try {
			await apiClient.delete(
				`/workplaces/${workplaceId}/case/type/${caseTypeId}`,
			);
			displaySuccess(
				t('component.caseType.caseTypeSuccessfullyDeleted') ?? 'Deleted',
			);
			onSuccess(false);
		} catch (e: any) {
			displayError(e);
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<CaseTypeDetailForm
			onSuccess={caseTypeId ? handleEdit : handleCreate}
			onRemove={handleRemove}
			workplaceId={workplaceId}
			defaultValues={defaultValues}
			isOnlyOneRoot={isOnlyOneRoot}
			caseTypeId={caseTypeId}
		/>
	);
};

export default CaseTypeDetail;
